<template>
  <div>
    <nav id="l-leftNav">
      <div>
        <header class="navhat">
          <a
            v-if="skipToContentId"
            class="_screenreader-action"
            :href="`#${skipToContentId}`"
            :title="i18n[locale].skipToContent"
            data-gtm-event="nfb_menu"
            data-gtm-nfb_detail="sidebar"
          >
            {{ i18n[locale].skipToContent }}
          </a>
          <a
            class="external-link"
            target="_blank"
            :href="i18n[locale].nfbWebsite.url"
            :title="i18n[locale].nfbWebsite.alt"
            data-gtm-event="nfb_menu"
            data-gtm-nfb_detail="sidebar"
          >
            <span v-html="i18n[locale].nfbWebsite.title" />
            <i class="icon icon-external" />
          </a>
          <label
            class="_screenreader-readable"
            for="nav-toggle-button"
          >
            {{ i18n[locale].toggleButton?.accessibilityLabel }}
          </label>
          <button
            id="nav-toggle-button"
            @click="close"
            class="reset l-toggleItem hidden-xs hidden-sm"
          >
            <span class="menuHam" />
          </button>
          <div class="svg-container">
            <svg
              class="eyecon-top-left"
              version="1.1"
              preserveAspectRatio="xMaxYMin"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              viewBox="0 0 85 50"
              xml:space="preserve"
            ><path d="M84.4,49.4L84.4,49.4C84.4,49.4,84.4,49.4,84.4,49.4C84.5,49.4,84.5,49.4,84.4,49.4L84.4,49.4c0.2,0,0.4,0,0.6,0v-50H60.6c0,0,0,0.1,0,0.1c0,10.1,6.3,18.6,15.1,22.1c-15.2-2-29.9-9.4-41.3-22.1c0,0,0.1-0.1,0.1-0.1H0.1c0,0,0,0.1-0.1,0.1C19.2,32.8,51.8,49.3,84.4,49.4" /></svg>
          </div>
        </header>

        <component
          :is="homeLink.tag"
          v-bind="homeLink.props"
          class="logo-container"
          :title="i18n[locale].nfbLogo.alt"
          data-gtm-event="nfb_menu"
          data-gtm-nfb_detail="sidebar"
        >
          <img
            :src="logoAsset"
            :alt="i18n[locale].nfbLogo.alt"
          >
        </component>

        <slot />
      </div>
    </nav>
    <nav id="l-leftNav-collapsed">
      <a
        v-if="skipToContentId"
        class="_screenreader-action"
        data-gtm-event="nfb_menu"
        data-gtm-nfb_detail="sidebar"
        :href="`#${skipToContentId}`"
        :title="i18n[locale].skipToContent"
      >
        {{ i18n[locale].skipToContent }}
      </a>
      <label
        class="_screenreader-readable"
        for="collapsed-nav-toggle-button"
      >
        {{ i18n[locale].toggleButton?.accessibilityLabel }}
      </label>
      <button
        id="collapsed-nav-toggle-button"
        @click="toggle"
        class="reset l-toggleItem"
      >
        <span class="menuHam" />
      </button>
      <div class="logos-container">
        <component
          :is="homeLink.tag"
          v-bind="homeLink.props"
          class="logo"
          data-gtm-event="nfb_menu"
          data-gtm-nfb_detail="sidebar"
          :title="i18n[locale].nfbLogo.alt"
        >
          <img
            class="vertical"
            src="./../../../assets/logo-vertical.svg"
            :alt="i18n[locale].nfbLogo.alt"
          >
          <img
            class="horizontal"
            :src="logoAsset"
            :alt="i18n[locale].nfbLogo.alt"
          >
        </component>
        <a
          class="external-link"
          rel="external"
          target="_blank"
          :href="i18n[locale].nfbWebsite.url"
          :title="i18n[locale].nfbWebsite.title"
          data-gtm-event="nfb_menu"
          data-gtm-nfb_detail="sidebar"
        >
          <i class="icon icon-external" />
        </a>
      </div>
      <slot name="mobile" />
    </nav>
  </div>
</template>

<script>
import enTranslations from './../../../i18n/en_CA/left-nav-header.json'
import frTranslations from './../../../i18n/fr_CA/left-nav-header.json'
import logo from './../../../assets/logo-horizontal.svg'

export default {
  name: 'LayoutLeftNav',
  props: {
    locale: {
      type: String,
      default: 'en'
    },
    i18n: {
      type: Object,
      default: () => ({
        en: enTranslations,
        fr: frTranslations
      })
    },
    homeLink: {
      type: Object,
      default: () => ({
        tag: 'a',
        props: {
          href: '#'
        }
      })
    },
    logoAsset: {
      type: String,
      default: logo
    },
    skipToContentId: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      isOpened: true,
      isForceCollapsed: false // because small device detected, reopen when large device detected
    }
  },
  methods: {
    open () {
      this.isOpened = true
      this.$emit('change', this.isOpened)
    },
    close () {
      this.isOpened = false
      this.$emit('change', this.isOpened)
    },
    toggle () {
      this.isOpened = !this.isOpened
      this.$emit('change', this.isOpened)
    },
    forceCollapse () {
      if (this.isOpened) {
        this.isForceCollapsed = true
        this.close()
      }
    },
    unforceCollapse () {
      if (this.isForceCollapsed) {
        this.isForceCollapsed = false
        this.open()
      }
    }
  }
}
</script>

<style lang="less">
@import "./../../../less/accessibility.less";
@import (reference) "./../../../less/shared/utilities.less";
@import "@{frontend_static_less}/public/layout/left-nav.less";

#l-leftNav ._screenreader-action, #l-leftNav-collapsed ._screenreader-action {
  left: 0;
  min-width: 150px;
  text-align: center;
  font-size: 1rem;
  font-family: @headings-font-family;
}

#l-leftNav {
  .external-link {
    font-family: @font-family-semi-bold;
    display: flex;
    align-items: baseline;
    color: inherit;

    .icon-external {
      margin-left: 5px;
    }
  }

  .navhat {
    .svg-container {
      position: absolute;
      left: 0;
      top: 0;
      max-height: 100%;
      z-index: -1;
      width: 72px;
      transform: scale(-1, 1);
      fill: var(--nfb-accent-color);
    }
  }

  ul {
    li {
      max-width: 130px;
      box-sizing: content-box;
    }
  }

  .active {
    pointer-events: none;
  }

  .menu-divider {
    width: 50px;
    height: 4px;
    background-color: @grey-600;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  &-collapsed {
    .logo {
      .horizontal {
        max-height: 2em;
      }
    }
  }
}
</style>
